import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteModuleMutation } from '@entities/Module';
import { paths } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { Button, Modal, toast } from '@shared/ui';

interface DeleteModuleConfirmProps {
  module: ModuleSummary;
  open: boolean;
  onClose: () => void;
}

/**
 * Подтверждение удаления модуля: владеет мутацией, тостом и переходом в «Модули».
 * Открывается из меню «⋯» страницы модуля (ModuleMenu) на desktop и mobile.
 */
export function DeleteModuleConfirm({ module: m, open, onClose }: DeleteModuleConfirmProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteMut = useDeleteModuleMutation();

  // Сбрасываем прошлую ошибку при каждом открытии диалога.
  useEffect(() => {
    if (open) deleteMut.reset();
    // deleteMut.reset стабилен в react-query; завязываемся только на open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (deleteMut.isPending) return;
    onClose();
  };

  const handleDelete = () => {
    deleteMut.mutate(m.id, {
      onSuccess: () => {
        onClose();
        toast({
          tone: 'success',
          title: t('moduleDetail.danger.toastTitle'),
          message: t('moduleDetail.danger.toastBody', { name: m.title }),
        });
        navigate(paths.words, { replace: true });
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      kicker={t('moduleDetail.danger.confirmKicker')}
      title={t('moduleDetail.danger.confirmTitle', { name: m.title })}
      width={460}
      footer={
        <>
          {deleteMut.isError ? (
            <div className="text-[12px] tracking-[0.02em] text-danger">{t('moduleDetail.danger.error')}</div>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="naked" size="sm" onClick={close} disabled={deleteMut.isPending}>
              {t('moduleDetail.danger.cancel')}
            </Button>
            <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? '…' : t('moduleDetail.danger.confirmCta')}
            </Button>
          </div>
        </>
      }
    >
      <p className="text-[14px] leading-relaxed text-muted">{t('moduleDetail.danger.confirmBody')}</p>
    </Modal>
  );
}
